import * as angular from "angular"
import { IGlobalAllocationsBindingModel } from "../dashboard/global-map/types"
import { IHealthySoilsAssessment, IHsaDrainage, IHsaSoilSeries } from "../hsa/types"
import { INitrogenAnalysis } from "../nitrogen/types"
import { getNitrogenAnalysisFromAssessment } from "../nitrogen/utils"
import { convertDatesToJsonUtc, datalayers, FieldViewModel, ILaboratory, IOmniaSession, IPcnAnalysis, isArrayEmpty, isNumber, ISoilAnalysis, ISoilUnitService, isTerraMapLayer, models, OmniaModuleId, OmniaObjectType, OmniaService, SharedDataService, SharedLayersService, SoilAnalysisType, SsmClassification } from "../omnia"
import { Fields } from "../plan/components/fields/Fields"
import { AnalysisType, IDataLayer, IDataLayerType, IFieldsService, IGenerationInput, IInterpretationGroup, INitrogenAnalysisAllocBindingModel, INitrogenAnalysisBindingModel, IPcnAnalysisAllocBindingModel, ISoilAnalysisAllocBindingModel, ISoilAnalysisOrder, ISoilService, LayerTypeId, PropertyInterpretation } from "../types"
import { SoilApi } from "./api"

export class SoilService extends OmniaService implements ISoilService {

    private readonly api = new SoilApi(this.apiInterface)
    public readonly settings = this.getSettings("soilanalysis")

    private _ssmClassification: SsmClassification[]

    private _fields: FieldViewModel[]
    private _activeFields: FieldViewModel[]

    private _nitrogenInclAssessments: INitrogenAnalysis[]

    private _morgan: boolean


    static $inject = [
        "OmniaSharedData",
        "OmniaFieldsService",
        "OmniaSession",
        "OmniaSoilUnitService",
        "OmniaDatalayersService",
    ]

    constructor(
        private readonly sharedData: SharedDataService,
        public readonly fieldSvc: IFieldsService,
        session: IOmniaSession,
        public units: ISoilUnitService,
        private datalayerSvc: SharedLayersService) {

        super(session, {
            name: "Soil",
            module: OmniaModuleId.Soil,
            dependencies: [sharedData, fieldSvc, datalayerSvc]
        })

    }


    shouldRefresh() {

        const changes = this.apiState.getChangesFromSource(
            this._changeNumber,
            s => (s == OmniaModuleId.Soil)
                || (s == OmniaModuleId.Fields)
                || (s == OmniaModuleId.Layers)
                || (s == "refresh-all")
                || (!s)
        )

        return !isArrayEmpty(changes)

    }


    getRefreshPromise(): ng.IPromise<any> {


        const { session, api } = this


        this._nitrogenInclAssessments = undefined

        return api.load(this.session).then(r => {

            this._fields = this.fieldSvc.getFields(true) || []
            this._activeFields = this._fields.filter(f => !f.disabled)

            const soil = this.sharedData.soil

            this._ssmClassification = soil?.ssmClassification || []

            if (session.holding.Region === models.RegionId.Scotland) {
                this._morgan = true
            }
            else {
                const morgan = api.getSoilAnalyses(s => s.P2O5Method === models.SoilP2O5Method.Morgan)
                this._morgan = !isArrayEmpty(morgan)
            }

            return r

        })


    }

    get fields() {
        return this._fields;
    }

    get activeFields() {
        return this._activeFields
    }

    get cropping(): FieldViewModel[] {
        return this.fieldSvc.cropping;
    }

    get enableMorganAnlysis() {
        return this._morgan
    }

    findField(fieldId: number, includeDisabled: boolean = true) {
        return this.fieldSvc.findField(fieldId, includeDisabled)
    }

    findCropping(croppingId: number) {
        return this.fieldSvc.findCropping(croppingId)
    }

    getSoilAnalyses(type?: SoilAnalysisType, terramap = false) {
        const f = getAnalysisTypeFilter(type, terramap)
        return this.api.getSoilAnalyses(f)
    }

    hasSsmAnalyses() {
        return this.api.hasSsmAnalyses()
    }

    getSsmClassification(): SsmClassification[] {
        return [...this._ssmClassification || []]
    }

    getAnalysisOrderInProgress(): boolean {
        return this.api.getAnalysisOrderInProgress();
    }

    getNitrogenAnalysis(includeFarmAssesments?: boolean): INitrogenAnalysis[] {

        if (!includeFarmAssesments) {
            return this.api.getNitrogenAnalysis();
        }

        if (!this._nitrogenInclAssessments) {
            this._nitrogenInclAssessments = this.getAllNitrogenAnalysis();
        }

        return this._nitrogenInclAssessments;

    }

    getHealthySoilsAssessments(): IHealthySoilsAssessment[] {
        return this.api.getHealthySoilsAssessments();
    }

    getPcnAnalysis(): IPcnAnalysis[] {
        return this.api.getPcnAnalysis();
    }

    getSnsAssessments() {
        return this.api.getSnsAssessments();
    }

    getHealthySoilsAssessmentSeries(): IHsaSoilSeries[] {
        return this.api.getHealthySoilsAssessmentSeries();
    }

    getHsaDrainage(): IHsaDrainage[] {
        return this.api.getHsaDrainage();
    }

    getPrimaryCultivations() {
        return this.api.getPrimaryCultivations();
    }

    getDrillTypes() {
        return this.api.getDrillTypes();
    }

    getFieldSoilAnalyses(fieldId: number, zones: boolean = true): ISoilAnalysis[] {

        function filter(a: ISoilAnalysis) {
            return (!a.TerraMap) && (!a.Type) && checkFieldAlloc(a, fieldId, zones)
        }

        return this.api.getSoilAnalyses(filter)

    }

    getFieldPcnAnalysis(fieldId: number): IPcnAnalysis[] {
        return this.api
            .getPcnAnalysis()
            .filter(sa => sa.Allocations && sa.Allocations.some(a => (a.FieldId === fieldId)))
    }

    getLaboratories(): ILaboratory[] {
        return this.api.getLaboratories();
    }

    getInterpretations(): IInterpretationGroup[] {
        return this.api.getInterpretations();
    }

    updateSoilAnalysisAlloc(model: ISoilAnalysisAllocBindingModel): ng.IPromise<any> {

        return this.api.updateSoilAnalysisAlloc(model)

    }

    saveSoilAnalysis(analysis: ISoilAnalysis, fieldsIds: number[], comments: PropertyInterpretation[]): ng.IPromise<ISoilAnalysis> {

        return this.api.saveSoilAnalysis(this.session, analysis, fieldsIds, comments)

    }

    deleteSoilAnalysis(id: number, deleteOtherGridSamples: boolean) {

        return this.api.deleteSoilAnalysis(id, deleteOtherGridSamples)


    }

    saveNitrogenAnalysis(model: INitrogenAnalysisBindingModel): ng.IPromise<number> {

        return this.api.saveNitrogenAnalysis(model)

    }

    updateNitrogenAnalysisAlloc(model: INitrogenAnalysisAllocBindingModel): ng.IPromise<any> {

        return this.api.updateNitrogenAnalysisAlloc(model)

    }

    deleteNitrogenAnalysis(id: number) {

        return this.api.deleteNitrogenAnalysis(id)

    }

    generateSoilAnalysis(fields: IGenerationInput[]): ng.IPromise<any> {

        const url = "/api/soils/soilanalysis/generate"

        return this.post(url, fields, { type: OmniaObjectType.SoilAnalysis })

    }

    saveHsa(healthySoilsAssessment: IHealthySoilsAssessment, fieldIds: number[], location: number[]): ng.IPromise<any> {

        const url = "/api/soils/healthysoilsassessment/save";

        const model = angular.extend({}, healthySoilsAssessment);

        convertDatesToJsonUtc(model);

        const data = {
            Assessment: model,
            FieldIds: fieldIds,
            Location: location
        }

        return this.post(url, data, { type: OmniaObjectType.PcnAnalysis })

    }

    unallocHsa(healthySoilAssessmentId: number, fieldId: number): ng.IPromise<any> {

        const url = "/api/soils/healthysoilsassessment/unalloc";

        const data = {
            Id: healthySoilAssessmentId,
            FieldId: fieldId
        }

        return this.post(url, data, { type: OmniaObjectType.HsAnlaysis })

    }

    allocHsa(fieldId: number, assessmentIds: number[]): ng.IPromise<any> {

        const url = "/api/soils/healthysoilsassessment/alloc";

        const data = {
            FieldId: fieldId,
            AssessmentIds: assessmentIds
        }

        return this.post(url, data, { type: OmniaObjectType.HsAnlaysis })

    }

    unallocSoilAnalysis(soilAnalysisId: number, fieldId: number): ng.IPromise<any> {

        const url = "/api/soils/soilanalysis/unalloc";

        const data = {
            Id: soilAnalysisId,
            FieldId: fieldId
        }

        return this.post(url, data, { type: OmniaObjectType.SoilAnalysis })

    }

    unallocNitrogenAnalysis(nitrogenAnalysisId: number, croppingId: number): ng.IPromise<any> {

        const url = "/api/soils/nitrogenanalysis/unalloc";

        const data = {
            Id: nitrogenAnalysisId,
            FieldId: croppingId
        }

        return this.post(url, data, { type: OmniaObjectType.NitrogenAnalysis })

    }

    saveGlobalAnalysisAlloc(model: IGlobalAllocationsBindingModel): ng.IPromise<any> {

        const url = "/api/soils/global/alloc"

        const type = model.AnalysisType == AnalysisType.Soil
            ? OmniaObjectType.SoilAnalysis
            : undefined

        return this.post(url, model, { type })


    }

    getLayerTypes() {
        return this.datalayerSvc.getLayerTypes()
    }

    getLayerType(typeId: LayerTypeId) {
        return this.datalayerSvc.getLayerTypes().find(t => t.TypeId == typeId)
    }

    getSoilLayers(loadZones: boolean, types: LayerTypeId[]): ng.IPromise<IDataLayer[]> {

        types = types || getSoilLayerTypes(this.datalayerSvc.getLayerTypes())

        return this.datalayerSvc.getHoldingLayers(types).then(layers => {

            if (loadZones) {

                const subset = layers.filter(l => !(l.Zones || isTerraMapLayer(l)))

                if (subset.length) {
                    return this.datalayerSvc.loadLayerZones(subset).then(() => layers)
                }

            }

            return layers

        })

    }

    getSoilAnalysisLayers(loadZones: boolean): ng.IPromise<IDataLayer[]> {
        return this.getSoilLayers(loadZones, [LayerTypeId.SoilAnalysis]);
    }

    getSoilTypeLayers(loadZones: boolean): ng.IPromise<IDataLayer[]> {
        return this.getSoilLayers(loadZones, [LayerTypeId.SoilType]);
    }


    /**
     * Returns a list of all nitrogen analysis, including SNS derived from farm assessments
     */
    private getAllNitrogenAnalysis(): INitrogenAnalysis[] {


        let analysis = this.api.getNitrogenAnalysis() || [];
        const sns = this.api.getSnsAssessments();

        if (sns) {
            analysis = analysis.concat(sns.map(s => getNitrogenAnalysisFromAssessment(this.holdingId, s)));
        }

        return analysis;

    }

    savePcnAnalysis(analysis: IPcnAnalysis, fieldsIds: number[]): ng.IPromise<number> {

        return this.api.savePcnAnalysis(this.session, analysis, fieldsIds)

    }

    deletePcnAnalysis(id: number, deleteOtherGridSamples: boolean): ng.IPromise<any> {

        return this.api.deletePcnAnalysis(id, deleteOtherGridSamples)


    }

    unallocPcnAnalysis(pcnAnalysisId: number, fieldId: number): ng.IPromise<any> {

        const url = "/api/soils/pcn/unalloc";

        const data = {
            Id: pcnAnalysisId,
            FieldId: fieldId
        }

        return this.post(url, data, { type: OmniaObjectType.PcnAnalysis })

    }

    updatePcnAnalysisAlloc(model: IPcnAnalysisAllocBindingModel): ng.IPromise<any> {

        const url = "/api/soils/pcn/allocate";

        return this.post(url, model, { type: OmniaObjectType.PcnAnalysis })


    }

    deleteHsa(id: number) {

        return this.api.deleteHsa(id)

    }

    orderSoilAnalysis(order: ISoilAnalysisOrder, selection: any): ng.IPromise<any> {
        const url = "/api/soils/order/save"
        const data = { Order: order, Selection: selection }
        return this.post(url, data)
    }

}

function getSoilLayerTypes(allTypes: IDataLayerType[]) {

    const tm = allTypes.filter(isTerraMapLayer).map(t => t.TypeId)

    return [
        LayerTypeId.SoilAnalysis,
        LayerTypeId.PcnAnalysis
    ].concat(tm)

}

function checkFieldAlloc(sa: { Allocations: { FieldId: number, ZoneAlloc: boolean }[] }, fieldId: number, zones: boolean) {

    return sa.Allocations && sa.Allocations.some(a => (a.FieldId === fieldId) && (zones || (!a.ZoneAlloc)))

}

function getAnalysisTypeFilter(type?: SoilAnalysisType, terramap?: boolean) {

    const filter1: (a: ISoilAnalysis) => boolean = isNumber(type)
        ? a => a.Type == type
        : a => true

    const filter2 = (!terramap)
        ? (a: ISoilAnalysis) => (!a.TerraMap) && filter1(a)
        : filter1

    return filter2

}
